import React from "react";
import Layout from "../components/Layout";
import useSeo from "../hooks/useSeo";
import { useCommon } from "../hooks/useApi";

// today's date in IST as dd-mm-yyyy (for instant header before data loads)
const todayIST = () => {
  const p = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date());
  return p.replace(/\//g, "-");
};

const PLACEHOLDER_ROWS = [
  { direct: "--", house: "--", ending: "--" },
  { direct: "--", house: "--", ending: "--" },
];

const CommonNumber = () => {
  const { data } = useCommon();
  useSeo(
    "Morning Sunday Teer Common Number Today | Shillong Morning Teer Common Number",
    "Check today's Morning Sunday Teer Common Number — Direct, House and Ending targets for Shillong Morning Teer with daily updates.",
    { breadcrumb: [ { name: "Home", path: "/" }, { name: "Common Number", path: "/common-numbers" } ] }
  );

  const date = data?.date || todayIST();
  const city = data?.city || "SHILLONG";
  const rows = data?.rows?.length ? data.rows : PLACEHOLDER_ROWS;
  const disclaimer =
    data?.disclaimer ||
    "The Morning Sunday Teer Common Number predictions are generated purely through historical data calculations and mathematical observation of past outcomes.";

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-center text-2xl font-bold text-[#1f2a37] mb-4">
          {date.replace(/-/g, "/")}
        </p>

        {/* Common number table */}
        <div className="border-2 border-black rounded-sm overflow-hidden max-w-md mx-auto mb-6">
          <div className="bg-[#e6e6e6] text-center py-2 text-xl font-medium border-b-2 border-black">
            {city}
          </div>
          {rows.map((row, idx) => (
            <React.Fragment key={idx}>
              <div className="grid grid-cols-3 bg-[#7fd0b8] font-semibold text-center border-b-2 border-black">
                <div className="py-2 border-r-2 border-black">Direct</div>
                <div className="py-2 border-r-2 border-black">House</div>
                <div className="py-2">Ending</div>
              </div>
              <div
                className={`grid grid-cols-3 text-center text-lg font-bold ${
                  idx < rows.length - 1 ? "border-b-2 border-black" : ""
                }`}
              >
                <div className="py-2 border-r-2 border-black">{row.direct}</div>
                <div className="py-2 border-r-2 border-black">{row.house}</div>
                <div className="py-2">{row.ending}</div>
              </div>
            </React.Fragment>
          ))}
        </div>

        <p className="text-xs text-gray-500 italic text-center max-w-md mx-auto mb-8">
          <b>Disclaimer:</b> {disclaimer}
        </p>

        {/* Article */}
        <article className="text-[#374151] leading-relaxed space-y-4">
          <h1 className="text-xl font-bold text-[#1f2a37]">
            Morning Sunday Teer Common Number Today: Tips & Analytical Targets
          </h1>
          <p>
            Welcome to the guide for tracking the <b>Morning Sunday Teer Result</b>{" "}
            and understanding the daily archery outcomes. Understanding the
            numbers is not mere guesswork — it involves statistical analysis,
            reviewing historical charts and tracking repetitive sequences to
            help you formulate a <b>Shillong Morning Teer Common Number</b>{" "}
            strategy.
          </p>
          <h2 className="text-lg font-bold text-[#1f2a37]">
            What Is a Common Number?
          </h2>
          <p>
            A common number is a calculated mathematical estimate — a target
            digit or set of digits that has statistically appeared more often
            based on recent data cycles. These are <b>not</b> guaranteed winning
            numbers; treat them as a compass, not a certainty.
          </p>
          <h2 className="text-lg font-bold text-[#1f2a37]">
            House, Ending & Direct
          </h2>
          <p>
            The <b>House</b> is the tens digit of a two-digit result (e.g. for
            82, House = 8). The <b>Ending</b> is the units digit (for 82, Ending
            = 2). A <b>Direct</b> number is an exact two-digit combination such
            as 55 or 89. Experienced players analyse House and Ending patterns
            over the previous results to refine their daily targets.
          </p>
          <p className="text-xs text-gray-500 italic">
            Please treat common numbers as part of the game's culture and
            tradition, not as a reliable prediction method. Play responsibly.
          </p>
        </article>
      </div>
    </Layout>
  );
};

export default CommonNumber;
